<template>
    <v-stepper v-model="currentStep">
      <v-stepper-header>
        <v-stepper-item
          step="1"
          title="Select Orders"
        ></v-stepper-item>
  
        <v-divider></v-divider>
  
        <v-stepper-item
          v-if="vendor.method == 'api'"
          :color="props.vendor.skip_verify == 0 ? 'error' : 'secondary'"
          step="2"
          title="Review & Submit"
        ></v-stepper-item>
  
        <v-stepper-item
          v-if="vendor.method == 'mail'"
          step="2"
          :title="`Send to ${vendor.vendor}`"
        ></v-stepper-item>

        <v-stepper-item
          v-if="vendor.method == 'ftp'"
          step="2"
          :title="`Submit to ${vendor.vendor}`"
        ></v-stepper-item>
  
        <v-divider></v-divider>
  
        <v-stepper-item
          step="3"
          title="Orders Placed"
        ></v-stepper-item>
      </v-stepper-header>
  
      <v-stepper-window>
        <!-- Step 1: Select Orders -->
        <v-stepper-window-item step="1">
          <v-card class="mb-4">
            <v-card-title class="d-flex align-center">
                <v-checkbox
                    v-model="selectAll"
                    hide-details
                    @change="toggleSelectAll"
                    label="Select All"
                  ></v-checkbox>
                <v-spacer></v-spacer>
              <v-btn
                v-if="showApiButtons && vendor.skip_verify == 0"
                color="primary"
                class="ml-2"
                :loading="verifying"
                :disabled="!selectedOrders.length"
                @click="verifyOrders"
              >
                Verify cost
              </v-btn>
              <v-btn
                v-if="showApiButtons && vendor.skip_verify == 1"
                color="primary"
                class="ml-2"
                :loading="submitting"
                :disabled="!selectedOrders.length"
                @click="submitOrders"
              >
                Submit Orders
              </v-btn>
              <v-btn
                v-if="vendor.method == 'mail'"
                color="primary"
                class="ml-2"
                :loading="sending"
                :disabled="!selectedOrders.length"
                @click="currentStep = 1"
              >
                Send Email
              </v-btn>
              <v-btn
                v-if="vendor.method == 'ftp'"
                color="primary"
                class="ml-2"
                :loading="sending"
                :disabled="!selectedOrders.length"
                @click="currentStep = 1"
              >
                Submit FTP
              </v-btn>
              <v-btn
                v-if="vendor.method !== 'api'"
                color="primary"
                class="ml-2"
                @click="exportOrders"
              >
                Export Orders
              </v-btn>
              <v-btn
                color="error"
                class="ml-2"
                @click="confirmDelete"
                :loading="deleting"
                :disabled="selectedOrders.length === 0"
              >
                Delete Orders
              </v-btn>
              <v-btn
                color="warning"
                class="ml-2"
                @click="openMarketplaceLinks"
                :disabled="props.orders.length === 0"
              >
                Open marketplace links
              </v-btn>
                <v-btn
                  color="secondary"
                  class="ml-2"
                  :disabled="!canAssignVendor"
                  :loading="assigning"
                  @click="assignVendor"
                >
                  Assign to
                </v-btn>
                <v-select
                  v-model="selectedVendorToAssign"
                  :items="vendorsList"
                  :menu-props="{maxHeight: 'unset'}"
                  item-title="vendor"
                  item-value="slug"
                  label=""
                  class="ml-4"
                  style="width: 0px"
                  density="comfortable"
                  hide-details
                ></v-select>
            </v-card-title>
            <v-card-text class="text-body-2 text-primary font-weight-bold d-flex align-center">
              {{ selectedOrders.length }} Selected Orders
              <v-spacer></v-spacer>
              <div class="text-body-2 text-primary font-weight-bold">Total: {{ totalItems }} orders</div>
              <pagination-links
                v-if="totalPages > 1"
                :current-page="currentPage"
                :total-pages="totalPages"
                @page-change="handlePageChange"
              />
            </v-card-text>
          </v-card>
          <order-list
            v-if="orders"
            :orders="orders"
            :loading="loading"
            :selected-orders="selectedOrders"
            @selection-changed="handleSelectionChanged"
            @order-assigned="$emit('order-assigned', $event)"
            @update-error="handleUpdateError"
          />
        </v-stepper-window-item>
  
        <!-- Step 2: Review & Edit -->
        <v-stepper-window-item v-if="vendor.method == 'api'" step="2">
           <v-card class="mb-4">
            <v-card-title class="d-flex align-center">  
              <v-checkbox
                v-model="selectAllVerified"
                @change="toggleVerifiedSelectAll"
                label="Select All"
              ></v-checkbox>
              <v-spacer></v-spacer>
              <v-btn
                variant="text"
                @click="currentStep = 0"
                >
                Back
              </v-btn>
              <v-btn
                v-if="showApiButtons"
                color="primary"
                :loading="submitting"
                :disabled="!hasValidOrders"
                @click="submitOrders"
              >
                Submit Orders ({{ selectedVerifiedOrders.length }} selected)
              </v-btn>
            </v-card-title>
          </v-card>
          <verified-orders-list
            v-if="verifiedOrders"
            :orders="verifiedOrders"
            :selected-orders="selectedVerifiedOrders"
            @selection-changed="handleVerifiedSelectionChanged"
          />
        </v-stepper-window-item>

        <v-stepper-window-item v-if="vendor.method == 'mail'" step="2">
          <v-card class="mb-4">
            <v-card-title class="d-flex align-center"> 
              <v-btn
                variant="text"
                @click="currentStep = 0"
                >
                Back
              </v-btn>
            </v-card-title>
          </v-card>
          <v-card>
            <v-card-title class="d-flex align-center">
              <span>Send Email to {{ vendor.vendor }}</span>
            </v-card-title>
            <v-card-text>
              <send-email-form
                :subject="`${vendor.vendor} - New orders - ${formatShortDate()}`"
                :to="vendor.email"
                :orders="selectedOrders"
                :loading="sending"
                @submit="submitEmailOrders"
              />
             </v-card-text>
          </v-card>

        </v-stepper-window-item>  
        <v-stepper-window-item v-if="vendor.method == 'ftp'" step="2">
          <v-card class="mb-4">
            <v-card-title class="d-flex align-center"> 
              <v-btn
                variant="text"
                @click="currentStep = 0"
                >
                Back
              </v-btn>
            </v-card-title>
          </v-card>
          <v-card>
            <v-card-title class="d-flex align-center">
              <span>Submit FTP to {{ vendor.vendor }}</span>
            </v-card-title>
            <v-card-text>
              <submit-ftp-form
                :orders="selectedOrders"
                :loading="sending"
                @submit="submitFtpOrders"
              />
             </v-card-text>
          </v-card>

        </v-stepper-window-item>  
  
        <!-- Step 3: Submit Orders -->
        <v-stepper-window-item step="3">
          <v-card v-if="vendor.method == 'api'">
            <v-card-title class="d-flex align-center">
              <span>Orders Submitted</span>
            </v-card-title>
            <v-card-text>
              <submitted-orders-list
                :orders="submittedOrders"
              />
            </v-card-text>
            <v-card-actions>
              <v-btn
                variant="text"
                @click="handleBackToOrders"
              >
                Back to orders
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card v-if="vendor.method == 'mail'">
            <v-card-title class="d-flex align-center">
              <span>Order email is sent to {{ vendor.vendor }} successfully!</span>
            </v-card-title>
            <v-card-actions>
              <v-btn
                variant="text"
                @click="handleBackToOrders"
              >
                Back to orders
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete {{ selectedOrders.length }} selected orders?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="showDeleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            @click="deleteOrders"
            :loading="deleting"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script setup>
  import { ref, computed, defineProps, defineEmits, onMounted } from 'vue'
  import OrderList from './OrderList.vue'
  import VerifiedOrdersList from './VerifiedOrdersList.vue'
  import SendEmailForm from '@/components/common/SendEmailForm.vue'
  import SubmitFtpForm from './SubmitFtpOrderForm.vue'
  import SubmittedOrdersList from './SubmittedOrdersList.vue'
  import PaginationLinks from '@/components/common/PaginationLinks.vue'
  import { orderService } from '@/services/orderService'
  import { useRoute } from 'vue-router'
  import { useVendorsStore } from '@/stores/vendorsStore'
  import { salesService } from '@/services/salesService'
  import { useAuthStore } from '@/stores/authStore'

  const route = useRoute()
  const vendorsStore = useVendorsStore()
  const authStore = useAuthStore()

  const props = defineProps({
    orders: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      required: true
    },
    totalItems: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    vendor: {
      type: Object,
      required: true
    }
  })
  
  const emit = defineEmits(['page-changed', 'update-error', 'reload-orders'])
  const currentStep = ref(0)
  const selectedOrders = ref([])
  const verifiedOrders = ref([])
  const selectedVerifiedOrders = ref([])
  const submitting = ref(false)
  const verifying = ref(false)
  const selectAll = ref(false)
  const selectAllVerified = ref(false)
  const sending = ref(false)
  const submittedOrders = ref([])
  const selectedVendorToAssign = ref()
  const assigning = ref(false)
  const deleting = ref(false)
  const showDeleteDialog = ref(false)
  onMounted(() => {
    currentStep.value = 0
    selectedOrders.value = []
    verifiedOrders.value = []
    selectedVerifiedOrders.value = []
    selectAll.value = false
    selectAllVerified.value = false
    switch (props.vendor.slug) {
      case 'cpw':
        selectedVendorToAssign.value = 'pf'
        break
      case 'am_auto':
        selectedVendorToAssign.value = 'trq'
        break
      case 'partscontainer':
        selectedVendorToAssign.value = 'pa'
        break
      default:
        selectedVendorToAssign.value = null
    }
  })

  const hasValidOrders = computed(() => {
    return verifiedOrders.value.some(order => order.success)
  })

  const showApiButtons = computed(() => props.vendor?.method === 'api')

  
  const handleSelectionChanged = (orderId) => {
    const orderIndex = selectedOrders.value.findIndex(o => o.ID === orderId)
    if (orderIndex === -1) {
      // Add order to selection
      const orderToAdd = props.orders.find(o => o.ID === orderId)
      if (orderToAdd) {
        selectedOrders.value.push(orderToAdd)
      }
    } else {
      // Remove order from selection
      selectedOrders.value.splice(orderIndex, 1)
    }
    // Update selectAll checkbox state
    selectAll.value = selectedOrders.value.length === props.orders.length
  }
  const handleVerifiedSelectionChanged = (orderId) => {
    const orderIndex = selectedVerifiedOrders.value.findIndex(o => o.sale.ID === orderId)
    if (orderIndex === -1) {
      // Add order to selection
      const orderToAdd = verifiedOrders.value.find(o => o.sale.ID === orderId)
      if (orderToAdd) {
        selectedVerifiedOrders.value.push(orderToAdd)
      }
    } else {
      // Remove order from selection
      selectedVerifiedOrders.value.splice(orderIndex, 1)
    }
    // Update selectAll checkbox state
    selectAllVerified.value = selectedVerifiedOrders.value.length === verifiedOrders.value.length
  }
  
  const toggleSelectAll = () => {
    if (selectAll.value) {
      selectedOrders.value = [...props.orders]
    } else {
      selectedOrders.value = []
    }
  }

  const toggleVerifiedSelectAll = () => {
    if (selectAllVerified.value) {
      selectedVerifiedOrders.value = [...verifiedOrders.value.filter(order => order.success)]
    } else {
      selectedVerifiedOrders.value = []
    }
  }
  
  const handlePageChange = (page) => {
    emit('page-changed', page)
  }
  
  const verifyOrders = async () => {
    verifying.value = true
    try {
      verifiedOrders.value = []
      selectedVerifiedOrders.value = []
      const response = await orderService.verifyOrdersCost(
        route.params.vendor,
        selectedOrders.value.map(order => order.ID)
      )
      verifiedOrders.value = response
      currentStep.value = 1
    } catch (error) {
      console.error('Error verifying orders:', error)
      verifiedOrders.value = [] // Reset to empty array on error
    } finally {
      verifying.value = false
    }
  }
  
  const submitOrders = async () => {
    
    submitting.value = true
      try {
        if (!props.vendor.skip_verify) {
          console.log('submitVerifiedOrders' , selectedVerifiedOrders.value.map(order => order.sale.ID))
          const response = await orderService.submitVerifiedOrders(
            route.params.vendor,
            selectedVerifiedOrders.value.map(order => order.sale.ID),
            authStore.user.username
          )
          submittedOrders.value = response
        } else {
          const response = await orderService.submitVerifiedOrders(
            route.params.vendor,
            selectedOrders.value.map(order => order.ID)
          )
          submittedOrders.value = response
        }
      currentStep.value = 2
      selectedOrders.value = []
      verifiedOrders.value = []
    } catch (error) {
      console.error('Error submitting orders:', error)
      emit('update-error', {
        error: 'Failed to submit orders'
      })
    } finally {
      submitting.value = false
    }
  }

  const submitEmailOrders = async (emailData) => {
    sending.value = true
    // emailData.orderIds = selectedOrders.value.map(order => order.ID).join(',')
    emailData.append('submited_by', authStore.user.username)
    try {
      await orderService.submitEmailOrders(
        route.params.vendor,
        emailData,
      )
      currentStep.value = 2
    } catch (error) {
      console.error('Error sending email:', error)
    } finally {
      sending.value = false
    }
  }

  const submitFtpOrders = async (ftpData) => {
    sending.value = true
    ftpData.append('submited_by', authStore.user.username)
    try {
      await orderService.submitFtpOrders(route.params.vendor, ftpData)
      currentStep.value = 2
    } catch (error) {
      console.error('Error submitting FTP:', error)
    } finally {
      sending.value = false
    }
  }

  const handleBackToOrders = () => {
    currentStep.value = 0
    selectedOrders.value = []
    verifiedOrders.value = []
    selectedVerifiedOrders.value = []
    selectAll.value = false
    selectAllVerified.value = false
    emit('reload-orders')
  }

  const openMarketplaceLinks = () => {
    // Use selectedOrders if any are selected, otherwise use all orders
    const ordersToOpen = selectedOrders.value.length > 0 ? selectedOrders.value : props.orders
    
    // Small delay between opening each link to prevent popup blocking
    ordersToOpen.forEach((order, index) => {
      setTimeout(() => {
        window.open(getMarketplaceUrl(order), '_blank')
      }, index * 100)
    })
  }

const getMarketplaceUrl = (sale) => {
  if (sale.marketplace === 'Amazon') {
    return `https://www.amazon.com/dp/${sale.listingid}`
  } else if (sale.marketplace === 'eBay') {
    return `https://ebay.com/itm/${sale.listingid}`
  }
  return ''
}

// Computed properties for vendor assignment
const vendorsList = computed(() => {
  return vendorsStore.vendors.filter(v => v.slug !== props.vendor?.slug)
})

const canAssignVendor = computed(() => {
  return selectedOrders.value.length > 0 && selectedVendorToAssign.value
})

const assignVendor = async () => {
  if (!selectedVendorToAssign.value) return
  
  assigning.value = true
  try {
    await orderService.assignVendor(
      selectedVendorToAssign.value,
      selectedOrders.value.map(order => order.ID),
      props.vendor.slug
    )
    emit('order-assigned', selectedOrders.value.map(order => order.ID))
    selectedOrders.value = []
  } catch (error) {
    console.error('Error assigning vendor:', error)
    emit('update-error', {
      error: 'Failed to assign vendor'
    })
  } finally {
    assigning.value = false
  }
}

// Delete handlers
const confirmDelete = () => {
  showDeleteDialog.value = true
}

const deleteOrders = async () => {
  deleting.value = true
  try {
    await salesService.deleteSales(selectedOrders.value.map(order => order.ID))
    emit('order-assigned', selectedOrders.value.map(order => order.ID))
    selectedOrders.value = []
  } catch (error) {
    console.error('Error deleting orders:', error)
  } finally {
    deleting.value = false
    showDeleteDialog.value = false
  }
}

const formatShortDate = () => {
  const date = new Date()
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const exportOrders = async () => {
  try {
    
    const blob = await orderService.exportOrders(route.params.vendor)
    
    // Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${props.vendor.vendor}-orders-${new Date().toISOString().split('T')[0]}.csv`)
    
    // Trigger download
    document.body.appendChild(link)
    link.click()
    
    // Cleanup
    link.parentNode.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Error exporting orders:', err)
    emit('update-error', {
      error: 'Failed to export orders'
    })
  }
}
  </script> 